import { useEffect, useRef } from 'react';
import type { UserMembership } from '@vtak/shared';
import { useMembershipQuery } from '@/queries/use-membership-query';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { workspaceClient } from '@/services/workspace-client';
import { takePendingJoin } from '@/join/pending-join';
import { JoinScreen } from './join-screen';
import { UnitSelector } from './unit-selector';
import { PlatformAdminPicker } from './platform-admin-picker';
import { PendingBanner } from './pending-banner';
import { MissionAreaSetupGate } from './mission-area-setup';
import vtakLogo from '@/assets/vtak-logo.png';

/**
 * Resolves the user's workspace before the app renders.
 *  - loading                    → branded skeleton
 *  - no memberships             → join screen (platform-admin: unit picker)
 *  - one approved unit          → straight into the app (zero clicks)
 *  - many approved              → unit selector (unless a persisted choice matches)
 *  - only pending               → observer mode (app + pending banner)
 *  - admin/platform-admin preview → straight into the selected non-member unit
 *  - admin, no shared mission AOIs → one-time area setup overlay (skippable)
 */
export function WorkspaceGate({ children }: { children: React.ReactNode }): React.JSX.Element {
  const query = useMembershipQuery();
  const selectedUnitId = useWorkspaceStore((s) => s.selectedUnitId);
  const setSelectedUnit = useWorkspaceStore((s) => s.setSelectedUnit);
  const setRole = useWorkspaceStore((s) => s.setRole);
  const platformAdmin = useWorkspaceStore((s) => s.platformAdmin);

  // A visitor who chose "join as member" on the unified-join screen stored the
  // code before login; redeem it once now that they're authenticated.
  const redeemed = useRef(false);
  useEffect(() => {
    if (redeemed.current) return;
    redeemed.current = true;
    const code = takePendingJoin();
    if (code) void workspaceClient.join(code).then(() => query.refetch());
  }, [query]);

  const memberships = query.data ?? [];
  const approved = memberships.filter((m) => m.status === 'approved');
  const pending = memberships.filter((m) => m.status === 'pending');
  const active = resolveActiveUnit(approved, selectedUnitId);
  const previewing = isAdminPreview(approved, selectedUnitId, platformAdmin);

  useEffect(() => {
    // An admin previewing a non-member unit keeps their selection; the server
    // authorizes the manifest request via inherited role.
    if (previewing) {
      setRole('admin');
      return;
    }
    if (!active) return;
    if (selectedUnitId !== active.unitId) setSelectedUnit(active.unitId);
    setRole(active.role);
  }, [active, previewing, selectedUnitId, setSelectedUnit, setRole]);

  if (query.isLoading) return <GateSkeleton />;
  if (previewing) return <>{children}</>;
  // Dev bypass without a database has no memberships endpoint — enter directly
  // rather than blocking on the join screen. With a DB, the server seeds a dev
  // membership so the normal path below runs.
  if (import.meta.env.VITE_DEV_AUTH_BYPASS === 'true' && memberships.length === 0) {
    return <>{children}</>;
  }
  // A platform-admin (Velocity super admin) with no membership browses into any
  // unit instead of hitting the join-code wall; the server authorizes the
  // manifest via the platform-admin bypass.
  if (approved.length === 0 && pending.length === 0) {
    return platformAdmin ? <PlatformAdminPicker /> : <JoinScreen />;
  }
  // A unit admin entering a workspace with no shared mission AOIs gets the
  // one-time area setup overlay (skippable, persisted per unit) over the app.
  if (active) {
    return (
      <>
        {active.role === 'admin' && <MissionAreaSetupGate unitId={active.unitId} />}
        {children}
      </>
    );
  }
  if (approved.length > 1) return <UnitSelector memberships={approved} />;

  return (
    <>
      <PendingBanner />
      {children}
    </>
  );
}

/**
 * True when the caller has selected a unit outside their own memberships and is
 * entitled to preview it — either a unit admin (of some unit) or a platform-admin
 * (who may enter any unit). The server enforces the same rule on the manifest.
 */
function isAdminPreview(
  approved: UserMembership[],
  selectedUnitId: string | null,
  platformAdmin: boolean,
): boolean {
  if (!selectedUnitId) return false;
  if (approved.some((m) => m.unitId === selectedUnitId)) return false;
  return platformAdmin || approved.some((m) => m.role === 'admin');
}

/** The single approved unit, or the approved unit matching a persisted choice */
function resolveActiveUnit(
  approved: UserMembership[],
  selectedUnitId: string | null,
): UserMembership | null {
  if (approved.length === 1) return approved[0] ?? null;
  return approved.find((m) => m.unitId === selectedUnitId) ?? null;
}

/** Branded full-screen skeleton shown while memberships resolve */
function GateSkeleton(): React.JSX.Element {
  return (
    <div className="fixed inset-0 z-[2000] grid place-items-center bg-[#0d0d0d]">
      <img src={vtakLogo} alt="VTAK" className="h-14 w-auto animate-pulse opacity-80" />
    </div>
  );
}
